import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent } from 'ag-grid-community';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Expander from '@amzn/meridian/expander';
import { INDEFINITE_TIME } from '@constants/strings';

import {
  CalculateNewOccupiedFacings,
  calculateSum,
  calculateUnitsToRemove,
  colDefs,
} from '@components/agile/utils/RecommendationTableUtils';
import { AgileRecommendationResult, AgileRecommendationTypes } from '@components/agile/types/AgileRecommendationTypes';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Alert from '@amzn/meridian/alert';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import getDateInUTC from '@components/agile/utils/GetDateUtils';

export interface AtRecommendationTableProps {
  tableKey: string;
  recommendation: AgileRecommendationResult;
  disableButtonState: (tableKey: string, newDisableButtonState: boolean) => void;
  recommendationDataHandler: (tableKey: string, rowData: AgileRecommendationTypes[]) => void;
  macroplanogramToFacingUnitsMap: { [p: string]: number };
  zoneDetails: AgileMacroplanogram | undefined;
  subcategoryName?: string; // Optional: for aggregate model subcategory display
}
const AtRecommendationTable = (props: AtRecommendationTableProps) => {
  const {
    tableKey,
    recommendation,
    disableButtonState,
    macroplanogramToFacingUnitsMap,
    zoneDetails,
    recommendationDataHandler,
  } = props;
  const [unitsToRemove, setUnitsToRemove] = useState<number>(0);
  const [totalRemovedFacings, setTotalRemovedFacings] = useState(0);
  const [rowData, setRowData] = useState<AgileRecommendationTypes[]>(recommendation.results);
  const [newOccupiedFacings, setNewOccupiedFacings] = useState<number>(
    macroplanogramToFacingUnitsMap[zoneDetails?.entryId ?? ''] || 0,
  );
  const [loader, setLoader] = useState<boolean>(true);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
    }),
    [],
  );

  useEffect(() => {
    const totalNewFacings = macroplanogramToFacingUnitsMap[zoneDetails?.entryId ?? ''] || 0;
    if (recommendation === undefined) {
      setLoader(true);
    } else {
      setLoader(false);
    }
    // eslint-disable-next-line max-len
    const removedFacingsSum = calculateSum(rowData, 'difference');
    setTotalRemovedFacings(removedFacingsSum);
    const calculatedOccupiedFacings = CalculateNewOccupiedFacings(
      totalNewFacings,
      recommendation.occupiedFacingUnits,
      removedFacingsSum,
    );
    setNewOccupiedFacings(calculatedOccupiedFacings);
    const unitsToRemoveResult = calculateUnitsToRemove(
      totalNewFacings,
      recommendation.maxFacingUnits,
      recommendation.occupiedFacingUnits,
      removedFacingsSum,
    );
    if (unitsToRemoveResult === 0 && calculatedOccupiedFacings === recommendation.maxFacingUnits) {
      disableButtonState(tableKey, false);
    } else {
      disableButtonState(tableKey, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macroplanogramToFacingUnitsMap]);

  useEffect(() => {
    const totalNewFacings = macroplanogramToFacingUnitsMap[zoneDetails?.entryId ?? ''] || 0;
    const facingsToRemove = calculateUnitsToRemove(
      totalNewFacings,
      recommendation.maxFacingUnits,
      recommendation.occupiedFacingUnits,
      totalRemovedFacings,
    );
    const calculatedOccupiedFacings = CalculateNewOccupiedFacings(
      totalNewFacings,
      recommendation.occupiedFacingUnits,
      totalRemovedFacings,
    );
    setNewOccupiedFacings(calculatedOccupiedFacings);
    if (facingsToRemove === 0 && calculatedOccupiedFacings === recommendation.maxFacingUnits) {
      setUnitsToRemove(0);
      disableButtonState(tableKey, false);
    } else {
      setUnitsToRemove(facingsToRemove);
      disableButtonState(tableKey, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation.maxFacingUnits, newOccupiedFacings, recommendation.occupiedFacingUnits, totalRemovedFacings]);

  const onCellValueChangedHandler = (event: CellValueChangedEvent<AgileRecommendationTypes>) => {
    if (event.rowIndex != null) {
      const newData = [...rowData];
      const rowIndex = newData.findIndex((item) => item.asin === event.data.asin);
      newData[rowIndex].difference = event.data.remainingFacings - event.data.availableFacings;
      newData[rowIndex].remainingCapacity = Math.round(
        (event.data.fohCapacity / event.data.availableFacings) * event.data.remainingFacings,
      );
      newData[rowIndex] = event.data;
      setRowData(newData);
      recommendationDataHandler(tableKey, newData);
      const removedFacingsSum = calculateSum(rowData, 'difference');
      setTotalRemovedFacings(removedFacingsSum);
      const totalNewFacings = macroplanogramToFacingUnitsMap[zoneDetails?.entryId ?? ''] || 0;
      const unitstoRemove = calculateUnitsToRemove(
        totalNewFacings,
        recommendation.maxFacingUnits,
        recommendation.occupiedFacingUnits,
        removedFacingsSum,
      );
      setUnitsToRemove(unitstoRemove);
    }
  };

  const [showTable, setShowTable] = useState(true);

  return loader ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <Expander
      open={showTable}
      onChange={setShowTable}
      title={
        props.subcategoryName
          ? `${zoneDetails?.macroplanogramName} - ${props.subcategoryName}`
          : zoneDetails?.macroplanogramName
      }
      type="list"
    >
      <div className="ag-theme-alpine">
        <Row spacing="400" alignmentHorizontal="justify" spacingInset="400">
          <Box type="outline" spacingInset="400">
            <Text type="h100">Zone Live Date: {getDateInUTC(Number(zoneDetails?.startTime))}</Text>
            <Text type="h100">
              Zone End Date:{' '}
              {Number(zoneDetails?.endTime) >= INDEFINITE_TIME
                ? 'Indefinite'
                : getDateInUTC(Number(zoneDetails?.startTime))}
            </Text>
          </Box>
          <Box type="outline" spacingInset="400">
            <Text type="h100">
              {`Existing Facings: ${recommendation.occupiedFacingUnits}/${recommendation.maxFacingUnits}`}
            </Text>
            <Text type="h100">{`New Total Facings: ${newOccupiedFacings}/${recommendation.maxFacingUnits}`}</Text>
            {newOccupiedFacings < recommendation.maxFacingUnits && (
              <Alert type="warning" size="small">
                <Text type="h100">Zone is not full</Text>
              </Alert>
            )}
            {unitsToRemove > 0 && (
              <Alert type="error" size="small">
                <Text type="h100">{`Facings to remove: ${unitsToRemove}`}</Text>
              </Alert>
            )}
          </Box>
        </Row>
        {recommendation.results.length !== 0 ? (
          <Row spacing="400" alignmentHorizontal="center" spacingInset="400">
            <div style={{ height: 'auto', width: 2000 }} className="ag-theme-alpine">
              {rowData && showTable && (
                <AgGridReact<AgileRecommendationTypes>
                  rowData={rowData}
                  domLayout="autoHeight"
                  columnDefs={colDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  enableCellTextSelection
                  onCellValueChanged={onCellValueChangedHandler}
                />
              )}
            </div>
          </Row>
        ) : (
          <Text type="h300" alignment="center">
            No recommendation found.
          </Text>
        )}
      </div>
    </Expander>
  );
};
export default AtRecommendationTable;
